import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Box, Button, Grid, Switch } from "@material-ui/core";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { GridRowModel } from "@mui/x-data-grid-pro";
import { useFormik } from "formik";
import moment from "moment";
import "moment-timezone";
import Checkbox from "@mui/material/Checkbox";
import CommonAutoCompleteField from "src/components/CommonAutoCompleteField/CommonAutoCompleteField";
import CommonButton from "src/components/CommonButton/CommonButton";
import CommonDataGridPage from "src/components/CommonDataGridPage/CommonDataGridPage";
import CommonTextFieldDate from "src/components/CommonTextFieldForDate/CommonTextFieldDate";
import { columns } from "./components/columns";
import { searchData } from "src/app-redux/common-actions";
import { useStyles } from "../PaymentsList/components/style";
import { NodCommercialRoute } from "../../nod-commercial.routes";
import { filteryByArr } from "src/common/utils/app.dropdowns";
import { filterBy } from "src/common/utils/helpers";
import CommonMaterialGrid from "src/components/CommonXGrid/CommonMaterialGrid";
import { Tooltip } from "@mui/material";
import { addNotificationAction } from "src/app-redux/settings/settingsSlice";
import { ax } from "src/common/api/apiClient";

function getApiErrorMessage(res: any, fallback: string): string {
  if (res?.error) return String(res.error);
  if (res?.message) return String(res.message);
  if (Array.isArray(res?.errors)) {
    const messages = res.errors.filter(Boolean).map(String);
    if (messages.length) return messages.join(", ");
  }
  return fallback;
}

function isApiSuccess(res: any): boolean {
  if (res?.isSuccess === false) return false;
  if (res?.isSuccess === true || res?.success === true || res?.status === 0) return true;
  if (Array.isArray(res?.errors) && res.errors.filter(Boolean).length) return false;
  // HTTP 200 with no explicit failure signal
  return true;
}

function getInvoicesNetAmountDisplay(row: any): number {
  const received = Number(row?.totalPaymentReceived) || 0;

  const totalDue = Number(row?.totalPaymentDue);
  const net = Number(row?.netAmount);
  const total = Number(row?.totalPayment);
  const sTax = Number(row?.sTaxAmount);
  const totalPlusTax = Number.isFinite(total) && Number.isFinite(sTax) ? total + sTax : NaN;

  const candidates = [totalDue, net, totalPlusTax, total].filter((n) =>
    Number.isFinite(n)
  ) as number[];
  const bestTotal = candidates.length ? Math.max(...candidates) : 0;

  return Math.max(0, bestTotal - received);
}

export function UnpaidInvoices(): React.ReactElement {
  const isExportString = localStorage.getItem("isAllowToExportData");
  const isExport = isExportString === "true";
  const classes = useStyles();
  const moduleName = NodCommercialRoute.title;
  const [paymentRecord, setpaymentRecord] = React.useState<GridRowModel[]>([]);
  const pageHeading = "Unpaid Invoices";
  const [loader, setLoader] = useState<any>(false);
  const [isCustomDate, setIsCustomDate] = useState<boolean>(false);
  const [selectedApprovalIds, setSelectedApprovalIds] = useState<number[]>([]);
  const dispatch = useDispatch();
  const startDatePickerRef: any = useRef(null);
  const endDatePickerRef: any = useRef(null);

  const toggleApprovalSelection = useCallback((paymentId: number, checked: boolean) => {
    if (!paymentId) return;
    setSelectedApprovalIds((prev) =>
      checked
        ? prev.includes(paymentId)
          ? prev
          : [...prev, paymentId]
        : prev.filter((id) => id !== paymentId)
    );
  }, []);

  const displayColumns = useMemo(
    () =>
      columns.map((column: any) =>
        column.accessorKey === "approval"
          ? {
              ...column,
              Cell: function ApprovalCell({ row }: any) {
                const paymentId = Number(row.original?.paymentId);
                return (
                  <Checkbox
                    color="primary"
                    size="small"
                    checked={selectedApprovalIds.includes(paymentId)}
                    onChange={(e) => toggleApprovalSelection(paymentId, e.target.checked)}
                    disabled={!paymentId}
                  />
                );
              },
            }
          : column.accessorKey === "paymentTypes"
          ? {
              ...column,
              Cell: function customCell({ renderedCellValue }: any) {
                return (
                  <Tooltip title={renderedCellValue} followCursor>
                    <span>{renderedCellValue}</span>
                  </Tooltip>
                );
              },
            }
          : column.accessorKey === "totalPayment"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return ` ${
                  row.original.totalPayment ? row.original.currency.toUpperCase() || "PKR " : ""
                } ${
                  row.original.totalPayment
                    ? row.original.totalPayment.toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                      })
                    : ""
                }`;
              },
            }
          : column.accessorKey === "netAmount"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                const displayNet = getInvoicesNetAmountDisplay(row.original);
                const cur = String(row.original?.currency || "PKR").toUpperCase();
                return ` ${cur} ${displayNet.toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })}`;
              },
            }
          : column.accessorKey === "sTaxAmount"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return ` ${
                  row.original?.sTaxAmount ? row.original.currency.toUpperCase() || "PKR " : ""
                } ${
                  row.original?.sTaxAmount
                    ? row.original?.sTaxAmount?.toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                      })
                    : ""
                }`;
              },
            }
          : column.accessorKey === "discount"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return ` ${
                  row.original?.discount ? row.original.currency.toUpperCase() || "PKR " : ""
                } ${
                  row.original?.discount
                    ? row.original?.discount?.toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                      })
                    : ""
                }`;
              },
            }
          : column
      ),
    [selectedApprovalIds, toggleApprovalSelection]
  );

  const handleAllowApproval = async () => {
    if (!selectedApprovalIds.length) {
      dispatch(
        addNotificationAction({
          message: "Please select at least one invoice to approve.",
          options: { variant: "warning" },
        })
      );
      return;
    }

    const payload = selectedApprovalIds.map((id) => ({
      id,
      approval: true,
    }));

    setLoader(true);
    try {
      const response = await ax.put("update-payment-approval-by-gm", payload, {
        headers: {
          "Content-Type": "application/json-patch+json",
          Accept: "*/*",
        },
      });
      const res: any = response?.data;

      if (!isApiSuccess(res)) {
        dispatch(
          addNotificationAction({
            message: getApiErrorMessage(res, "Failed to update payment approval."),
            options: { variant: "error" },
          })
        );
        return;
      }

      if (!(res?.isSuccess === true && res?.status === 0 && res?.successMessage)) {
        dispatch(
          addNotificationAction({
            message: res?.successMessage || "Payment approval updated successfully.",
            options: { variant: "success" },
          })
        );
      }
      setSelectedApprovalIds([]);
      await handleSearch();
    } catch (error: any) {
      dispatch(
        addNotificationAction({
          message: getApiErrorMessage(
            error?.response?.data,
            error?.message || "Failed to update payment approval."
          ),
          options: { variant: "error" },
        })
      );
    } finally {
      setLoader(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      inProgressVehiclesDataOnly: true,
      filterBy: "today",
      vehicleStatusId: 10,
      startDate: moment(new Date(new Date().setDate(new Date().getDate())).setHours(0, 0)).format(
        "YYYY-MM-DDTHH:mm"
      ),
      endDate: moment(new Date(new Date().setDate(new Date().getDate())).setHours(23, 59)).format(
        "YYYY-MM-DDTHH:mm"
      ),
    },
    onSubmit: () => {
      // Search is triggered via the Search button (handleSearch).
    },
  });
  const { values, setValues } = formik;

  const handleSearch = async () => {
    const { filterBy: filterByValue, startDate, endDate, inProgressVehiclesDataOnly } = values;
    const body = {
      filterBy: filterByValue || null,
      startDate: startDate || null,
      endDate: endDate || null,
      inProgressVehiclesDataOnly: inProgressVehiclesDataOnly || false,
      paid: false,
    };
    setpaymentRecord([]);
    setSelectedApprovalIds([]);
    setLoader(true);
    try {
      const results: any = await dispatch(searchData("/get-all-payments", JSON.stringify(body)));
      const apiRecord = results;
      if (apiRecord) {
        const transformedData = apiRecord.map((data: any) => {
          return { ...data, id: data.paymentId ? data.paymentId : Math.random() };
        });
        setpaymentRecord(transformedData);
      }
    } catch (error) {
      dispatch(
        addNotificationAction({
          message: "Failed to load unpaid invoices.",
          options: { variant: "error" },
        })
      );
    }
    setLoader(false);
  };

  const [resetHandler, setResetHandler] = useState(false);

  const resetSearch = () => {
    setValues(formik.initialValues);
    setIsCustomDate(false);
    setSelectedApprovalIds([]);
    setResetHandler(true);
  };

  useEffect(() => {
    formik.setFieldValue("filterBy", "today");
    handleSearch();
    setResetHandler(false);
  }, [resetHandler]);

  const filterByAction = (data: any) => {
    formik.setFieldValue("filterBy", data.value);
    if (data && data.value) {
      const res = filterBy(data);
      if (res?.isCustomDate) {
        setIsCustomDate(true);
        formik.setFieldValue("startDateCustom", res?.startDate);
        formik.setFieldValue("endDateCustom", res?.endDate);
      } else {
        setIsCustomDate(false);
      }
      formik.setFieldValue("startDate", res?.startDate);
      formik.setFieldValue("endDate", res?.endDate);
    }
  };

  const exportHandler = async (row: any) => {
    dispatch(
      addNotificationAction({
        message: "Your download will begin shortly",
        options: { variant: "success" },
      })
    );
    const exportedRecords = await row?.map((data: any) => {
      return {
        "Serial #": data?.serialNumber,
        "Invoice Date": data?.invoiceDate
          ? moment(new Date(data.invoiceDate)).format("DD-MM-YYYY")
          : "",
        Vehicle: data?.registrationNumber,
        "VIR #": data?.virNo,
        "Container No": data?.containerNo,
        "BL Number": data?.billNumber || "",
        "Cargo Type": data?.cargoType,
        "Type of Payments": data?.paymentTypes,
        "Total Payment": `PKR ${data?.totalPayment?.toLocaleString(undefined, {
          maximumFractionDigits: 2,
        })}`,
        "Net Amount": `PKR ${getInvoicesNetAmountDisplay(data).toLocaleString(undefined, {
          maximumFractionDigits: 2,
        })}`,
        "Tax Amount": `PKR ${data?.sTaxAmount?.toLocaleString(undefined, {
          maximumFractionDigits: 2,
        })}`,
        Discount: `PKR ${data?.discount?.toLocaleString(undefined, {
          maximumFractionDigits: 2,
        })}`,
        "Payment Status": data?.paymentStatus,
      };
    });
    return exportedRecords;
  };

  return (
    <>
      <CommonDataGridPage
        module={moduleName}
        heading={pageHeading}
        GridDataComp={
          <CommonMaterialGrid
            rows={paymentRecord}
            columns={displayColumns}
            loader={loader}
            defaultPerPageRecord={15}
            height={550}
            isExport={isExport}
            deletedRow={true}
            exportHandler={exportHandler}
            filename={`Unpaid_invoices_${Date.now()}`}
            topToolbarRightActions={
              <Button
                variant="contained"
                disabled={selectedApprovalIds.length === 0 || loader}
                onClick={handleAllowApproval}
                style={{
                  backgroundColor: "#72CA00",
                  color: "#ffffff",
                }}
              >
                Allow
              </Button>
            }
          />
        }
      >
        <>
          <Grid spacing={2} container direction="row">
            <Grid item xs={12} md={3} lg={2}>
              <CommonAutoCompleteField
                cssClassName="customTextField"
                error={formik.touched.filterBy && Boolean(formik.errors.filterBy)}
                helperText={formik.touched.filterBy && formik.errors.filterBy}
                label="Filter by"
                placeholder="Filter by"
                name="filterBy"
                disableClearable={true}
                optionsType={filteryByArr}
                getOptionLabel={(option: any) => option.label || null}
                onChange={(row: any) => {
                  row ? setValues({ ...values, filterBy: row.value || "" }) : "";
                  if (row === null) {
                    formik.setFieldValue("filterBy", null);
                  }
                  filterByAction(row);
                }}
                value={
                  filteryByArr.find(({ value }: any) => value === formik.values.filterBy) || ""
                }
              />
            </Grid>
            <Grid item xs={12} md={3} lg={2} className={!isCustomDate ? classes.hideDatesGrid : ""}>
              <CommonTextFieldDate
                cssClassName="customTextField"
                label={"Start Date"}
                name={"startDate"}
                datePickerRef={startDatePickerRef}
                value={formik.values.startDate}
                onChange={(e: any) => {
                  formik.setFieldValue("startDate", moment(e).format("YYYY-MM-DDTHH:mm"));
                  formik.setFieldValue("endDate", "");
                }}
                maxDate={moment().format("YYYY-MM-DDTHH:mm")}
              />
            </Grid>
            <Grid item xs={12} md={3} lg={2} className={!isCustomDate ? classes.hideDatesGrid : ""}>
              <CommonTextFieldDate
                cssClassName="customTextField"
                label={"End Date"}
                name={"endDate"}
                datePickerRef={endDatePickerRef}
                value={formik.values.endDate}
                onChange={(e: any) => {
                  formik.setFieldValue("endDate", moment(e).format("YYYY-MM-DDTHH:mm"));
                }}
                minDate={formik.values.startDate}
                maxDate={moment().format("YYYY-MM-DDTHH:mm")}
              />
            </Grid>
            <Grid item xs={12} md={isCustomDate ? 3 : 4} lg={2} style={{ paddingRight: 0 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(formik.values.inProgressVehiclesDataOnly)}
                    onChange={formik.handleChange}
                    name="inProgressVehiclesDataOnly"
                    color="primary"
                  />
                }
                label="In Progress Vehicles"
              />
            </Grid>

            <Grid item xs={12} md={isCustomDate ? 12 : 5} lg={isCustomDate ? 4 : 8} direction="row">
              <Box
                textAlign="right"
                mt={1}
                mb={1}
                className="terminalChargesSearchBtns"
                style={{ float: "right" }}
              >
                <CommonButton cssClass="customSubmitBtn" onClick={() => resetSearch()}>
                  Reset
                </CommonButton>
                <CommonButton cssClass="customSubmitBtn" isGreen onClick={handleSearch}>
                  Search
                </CommonButton>
              </Box>
            </Grid>
          </Grid>
        </>
      </CommonDataGridPage>
    </>
  );
}
